import { create } from 'zustand'

type Theme = 'dark' | 'light'

interface ThemeStore {
  theme: Theme
  toggle: () => void
  /** Called after login — syncs server-side preference into local state */
  applyServerTheme: (theme: string | null | undefined) => void
}

const saved = (localStorage.getItem('ds_theme') as Theme) || 'dark'

const THEME_COLOR: Record<Theme, string> = {
  light: '#F5F2EB',
  dark: '#090A0C',
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme)
  localStorage.setItem('ds_theme', theme)
  const content = THEME_COLOR[theme]
  let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = content
}

// Apply immediately on page load (before React mounts)
applyTheme(saved)

export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: saved,
  toggle: () => {
    const next = get().theme === 'dark' ? 'light' : 'dark'
    applyTheme(next)
    set({ theme: next })
    // B1-#12: persist to backend (fire-and-forget; localStorage is the source of truth on load)
    import('../api/client').then(({ usersApi }) => usersApi.saveTheme(next)).catch(() => {})
  },
  applyServerTheme: (serverTheme) => {
    // B1-#12: on login, if server preference differs from localStorage, server wins
    if (!serverTheme) return
    const theme = (serverTheme === 'light' ? 'light' : 'dark') as Theme
    if (theme !== get().theme) {
      applyTheme(theme)
      set({ theme })
    }
  },
}))
